import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as Chevron } from 'assets/icons/svg/arrow-down.svg';
import { useFormContext } from 'context/FormDrawerContext/FormDrawerContext';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { CornerStyle } from 'context/ThemeContext/ThemeContext';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../LoadingIndicator';
import useWindowSize from 'hooks/useWindowSize';
import useDropdownPosition from 'hooks/useDropdownPosition';
import Scrollbar from 'utils/Scrollbar';

type OptionType = {
  value: string;
  label: string;
};

type SearchableDropDownProps = {
  type: 'text' | 'number';
  selected: OptionType | undefined;
  name?: string;
  styles?: string;
  required?: boolean;
  placeholder?: string;
  hideChevron?: boolean;
  autoComplete?: 'on' | 'off';
  customNoResultsMessage?: string;
  options: OptionType[] | undefined;
  ignoreSpecialCharacters?: boolean;
  onChange?: (option: OptionType) => void;
  fetchRecords?: (query: string) => Promise<any>;
  fetchProductsTested?: (query: string) => Promise<any>;
  scrollIntoView?: {
    enabled: boolean;
    parentHeight: number;
    parentContainerId: string;
  };
  allowedCustomValue?: boolean;
};

const SearchableDropDown: React.FC<SearchableDropDownProps> = ({
  type,
  name,
  styles,
  options,
  selected,
  required,
  onChange,
  hideChevron,
  placeholder,
  fetchRecords,
  scrollIntoView,
  allowedCustomValue,
  fetchProductsTested,
  customNoResultsMessage,
  ignoreSpecialCharacters,
  autoComplete = 'on',
}) => {
  const [borderRadius, setBorderRadius] = useState<string>('');
  const [showDropdown, toggleDropdown] = useState<boolean>(false);
  const [scrollbarOpacity, setScrollbarOpacity] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isServerSearch, setIsServerSearch] = useState<boolean>(false);
  const [serverOptions, setServerOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);
  const windowSize = useWindowSize();
  const { inputStyle } = useThemeContext();
  const { setNextDisabled } = useFormContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'searchableDropDown',
  });

  const handleValueSelection = (value: string) => {
    setSearchTerm(value);
    toggleDropdown(false);
    onChange &&
      onChange({
        label: value,
        value: value,
      });
    setNextDisabled(false);
  };

  const filteredOptions = options?.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (selected?.value) setSearchTerm(selected.label);
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | FocusEvent): void => {
      const target = event.target as Node;
      // The menu is rendered in a portal (outside containerRef), so clicks on an
      // option must be treated as "inside" or the clear-on-blur logic below would
      // wipe the just-made selection.
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        toggleDropdown(false);

        // Keep existing selection on blur when it matches current search term (e.g. selected from server results)
        if (selected?.value && selected.value === searchTerm) {
          return;
        }

        // Handle value selection on click outside
        if (searchTerm && allowedCustomValue) {
          handleValueSelection(searchTerm);
        } else if (
          !allowedCustomValue &&
          !fetchProductsTested &&
          !filteredOptions?.find((opt) => opt.label === searchTerm) &&
          !serverOptions?.find((opt) => opt.label === searchTerm)
        ) {
          // Clear selection if custom values aren't allowed and no match in static or server options
          setSearchTerm('');
          onChange && onChange({ value: '', label: '' });
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('focusin', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('focusin', handleClickOutside);
    };
  }, [
    searchTerm,
    allowedCustomValue,
    filteredOptions,
    serverOptions,
    selected,
    onChange,
  ]);

  useEffect(() => {
    if (showDropdown) {
      setTimeout(() => setScrollbarOpacity(100), 150);
      if (
        scrollIntoView?.enabled &&
        scrollIntoView?.parentContainerId &&
        containerRef?.current
      ) {
        const bottomSpace =
          scrollIntoView.parentHeight -
          containerRef.current?.getBoundingClientRect().bottom;
        if (bottomSpace < 0) {
          document
            .getElementById(scrollIntoView.parentContainerId)
            ?.scrollBy({ top: bottomSpace * -1 + 24, behavior: 'smooth' });
        }
      }
    } else {
      setTimeout(() => setScrollbarOpacity(0), 0);
    }
  }, [showDropdown, scrollIntoView, windowSize, containerRef]);

  useEffect(() => {
    if (inputStyle === CornerStyle.SQUARE_CORNERS) setBorderRadius('0px');
    else if (inputStyle === CornerStyle.ROUNDED_CORNERS)
      setBorderRadius('10px');
    else setBorderRadius('26px');
  }, [inputStyle]);

  const getDesiredHeight = () => {
    if (loading) return 96;
    const rowHeight = 40; // matches the h-10 option rows
    const verticalPadding = 16; // matches the py-2 on the list
    const optionsToDisplay = isServerSearch ? serverOptions : filteredOptions;
    // At least one row (the "no results" / custom-value row) is always rendered.
    const rows = Math.max(optionsToDisplay?.length || 1, 1);
    // Uncapped up to a sensible max; the position hook further clamps this to the
    // space available in the viewport so the menu scrolls instead of clipping.
    return Math.min(rows * rowHeight + verticalPadding, 280);
  };

  const position = useDropdownPosition(
    containerRef,
    showDropdown,
    getDesiredHeight(),
    [
      windowSize,
      borderRadius,
      loading,
      isServerSearch,
      serverOptions.length,
      filteredOptions?.length,
    ]
  );

  const getCornerStyle = () => {
    switch (inputStyle) {
      case CornerStyle.FULL_ROUND:
        return 'rounded-[26px]';
      case CornerStyle.ROUNDED_CORNERS:
        return 'rounded-[10px]';
      case CornerStyle.SQUARE_CORNERS:
        return 'rounded-none';
      default:
        return 'rounded-[26px]';
    }
  };

  const fetchServerResults = async (query: string) => {
    if (fetchProductsTested) {
      if (query.length < 3) {
        setLoading(false);
        return;
      }
      const response = await fetchProductsTested(
        ignoreSpecialCharacters ? query.replace(/[^a-zA-Z0-9\s]/g, '') : query
      );
      if (response && response.length === 0) setServerOptions([]);
      else {
        setServerOptions(
          response?.map((item: any) => ({
            label: item.name,
            value: item.upc,
          }))
        );
      }
      setLoading(false);
    }
    if (fetchRecords) {
      const response = await fetchRecords(query);
      setServerOptions(
        response?.map((item: any) => ({
          value: item.text,
          label: item.text,
        }))
      );
      setLoading(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    toggleDropdown(true);

    if (fetchProductsTested) {
      onChange && onChange({ value: value, label: value });
    } else {
      if (value === '') {
        onChange && onChange({ value: '', label: '' });
        return;
      }
      if (value !== selected?.label)
        onChange && onChange({ value: '', label: '' });
    }

    const wordCount = value.trim().length;
    if (wordCount > 2) {
      setLoading(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setIsServerSearch(true);
      debounceRef.current = window.setTimeout(() => {
        fetchServerResults(value);
      }, 1500); // Adjust debounce delay as needed
    } else {
      setIsServerSearch(false);
    }
  };

  const optionsToRender = isServerSearch ? serverOptions : filteredOptions;

  return (
    <div
      ref={containerRef}
      className={`
        not-draggable relative w-full h-input flex items-center mt-0.5 border-2 border-solid border-input transition-all ease-linear duration-150 cursor-pointer
        [&_input]:z-20 [&_input]:w-full [&_input]:border-0 [&_input]:bg-transparent
        [&_input]:active:bg-transparent [&_input]:active:outline-none
        [&_input]:hover:bg-transparent [&_input]:hover:outline-none
        ${
          selected?.label || searchTerm || showDropdown
            ? 'bg-transparent'
            : 'bg-lightgray'
        }
        ${getCornerStyle()} ${styles}
      `}
      style={{
        borderTopLeftRadius: borderRadius,
        borderTopRightRadius: borderRadius,
        borderBottomLeftRadius: showDropdown ? '0' : borderRadius,
        borderBottomRightRadius: showDropdown ? '0' : borderRadius,
      }}
    >
      <span
        className={`z-40 absolute mx-6 text-muted transition-all duration-150 pointer-events-none ${
          selected?.label || searchTerm
            ? 'text-xxxs px-0.5 bg-white -translate-x-0.5 -translate-y-[25px] opacity-100'
            : 'text-[0.9rem] bg-transparent translate-x-0 translate-y-0 opacity-80'
        }`}
      >
        {placeholder}
      </span>
      <div
        className='w-full relative'
        onClick={() => toggleDropdown(!showDropdown)}
      >
        <input
          ref={inputRef}
          name={name}
          type={type}
          width='100%'
          maxLength={50}
          value={searchTerm}
          required={required}
          aria-label={placeholder}
          autoComplete={autoComplete}
          onChange={handleSearchChange}
          onClick={() => !showDropdown && toggleDropdown(true)}
          className='!w-[calc(100%_-_2.5rem)] px-6 text-ellipsis overflow-hidden cursor-pointer'
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (type === 'number' && (event.key === 'e' || event.key === 'E')) {
              event.preventDefault();
            }
          }}
        />
        <button
          type='button'
          tabIndex={hideChevron ? -1 : 0}
          aria-label={t('ariaToggleDropdown')}
          className='absolute -top-1 right-3 w-8 h-8 flex items-center justify-center focus-visible:outline focus-visible:outline-black/60'
          onClick={() => toggleDropdown(!showDropdown)}
        >
          <Chevron
            aria-hidden={hideChevron ? true : false}
            className={`cursor-pointer duration-150 
              ${showDropdown ? 'rotate-180' : 'rotate-0'}
              ${hideChevron ? 'hidden' : 'block'}
            `}
            width={24}
            height={24}
          />
        </button>
        {showDropdown &&
          position &&
          createPortal(
            <ul
              ref={menuRef}
              onClick={(event) => event.stopPropagation()}
              className='not-draggable fixed z-[9999] shadow-dropdown bg-white overflow-auto py-2'
              style={{
                left: position.left,
                width: position.width,
                top: position.top,
                bottom: position.bottom,
                maxHeight: position.maxHeight,
                borderTopLeftRadius:
                  position.placement === 'top' ? borderRadius : '0',
                borderTopRightRadius:
                  position.placement === 'top' ? borderRadius : '0',
                borderBottomLeftRadius:
                  position.placement === 'bottom' ? borderRadius : '0',
                borderBottomRightRadius:
                  position.placement === 'bottom' ? borderRadius : '0',
              }}
            >
              {loading ? (
                <LoadingIndicator size='40px' styles='!mt-4' />
              ) : (
                <Scrollbar
                  style={{ background: '#FFFFFF', opacity: scrollbarOpacity }}
                >
                  {optionsToRender?.map((option: OptionType, index: number) => (
                    <li
                      tabIndex={showDropdown ? 0 : -1}
                      key={`dropdown-item-${option.value}-${index}`}
                      className='w-full h-10 flex items-center text-left px-6 leading-6 bg-white cursor-pointer hover:bg-lightgray focus-visible:bg-lightgray'
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          setSearchTerm(option.label);
                          toggleDropdown(false);
                          onChange && onChange(option);
                        }
                      }}
                      onClick={() => {
                        setSearchTerm(option.label);
                        toggleDropdown(false);
                        onChange && onChange(option);
                      }}
                    >
                      <div className='w-full flex flex-col items-center justify-center'>
                        <div className='w-full text-sm leading-1.5 overflow-hidden whitespace-nowrap text-ellipsis'>
                          {(() => {
                            if (!searchTerm) return <>{option.label}</>;
                            const normalize = (str: string) =>
                              ignoreSpecialCharacters
                                ? str.replace(/[^a-zA-Z0-9]/g, '')
                                : str;
                            const originalText = option.label;
                            const lowerOriginal = originalText.toLowerCase();
                            const normalizedOriginal = normalize(lowerOriginal);
                            const normalizedSearch = normalize(
                              searchTerm.toLowerCase()
                            );
                            const matchIndex =
                              normalizedOriginal.indexOf(normalizedSearch);
                            if (matchIndex === -1) return originalText;
                            let currentNormalizedIndex = 0;
                            let start = -1;
                            let end = -1;
                            for (let i = 0; i < originalText.length; i++) {
                              const char = lowerOriginal[i];
                              if (
                                !ignoreSpecialCharacters ||
                                /[a-zA-Z0-9]/.test(char)
                              ) {
                                if (currentNormalizedIndex === matchIndex)
                                  start = i;
                                if (
                                  currentNormalizedIndex ===
                                  matchIndex + normalizedSearch.length - 1
                                ) {
                                  end = i + 1;
                                  break;
                                }
                                currentNormalizedIndex++;
                              }
                            }
                            if (start === -1 || end === -1) return originalText;
                            return (
                              <>
                                {originalText.slice(0, start)}
                                <strong>
                                  {originalText.slice(start, end)}
                                </strong>
                                {originalText.slice(end)}
                              </>
                            );
                          })()}
                        </div>
                        {fetchProductsTested && (
                          <span className='w-full text-xxs leading-[120%] text-muted text-ellipsis overflow-hidden whitespace-nowrap'>
                            {(() => {
                              const normalize = (str: string) =>
                                ignoreSpecialCharacters
                                  ? str
                                      .replace(/[^a-zA-Z0-9\s]/g, '')
                                      .toLowerCase()
                                  : str.toLowerCase();
                              const renderWithHighlight = (
                                text: string
                              ): (string | React.JSX.Element)[] => {
                                if (!searchTerm) return [text];
                                const normalizedText = normalize(text);
                                const normalizedSearchTerm =
                                  normalize(searchTerm);
                                let result: (string | React.JSX.Element)[] = [];
                                let buffer = '';
                                let index = 0;
                                for (let i = 0; i < text.length; i++) {
                                  buffer += text[i];
                                  const normalizedBuffer = normalize(buffer);
                                  if (
                                    normalizedBuffer.endsWith(
                                      normalizedSearchTerm
                                    ) &&
                                    normalizedText
                                      .slice(index, i + 1)
                                      .replace(/[^a-zA-Z0-9\s]/g, '')
                                      .toLowerCase()
                                      .endsWith(normalizedSearchTerm)
                                  ) {
                                    const highlightStart =
                                      i - buffer.length + 1;
                                    if (highlightStart > 0)
                                      result.push(
                                        text.slice(index, highlightStart)
                                      );
                                    result.push(
                                      <strong key={i}>
                                        {text.slice(highlightStart, i + 1)}
                                      </strong>
                                    );
                                    index = i + 1;
                                    buffer = '';
                                  }
                                }
                                if (index < text.length)
                                  result.push(text.slice(index));
                                return result;
                              };
                              return renderWithHighlight(option.value);
                            })()}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                  {!optionsToRender?.length &&
                    !loading &&
                    !allowedCustomValue && (
                      <li
                        key='no-result'
                        className='w-full h-10 min-h-10 flex items-center px-6 cursor-pointer'
                      >
                        <p className='w-full text-center font-semibold text-sm text-muted opacity-60'>
                          {customNoResultsMessage
                            ? customNoResultsMessage
                            : t('noOptions')}
                        </p>
                      </li>
                    )}
                  {!optionsToRender?.length &&
                    !loading &&
                    allowedCustomValue && (
                      <li
                        tabIndex={showDropdown ? 0 : -1}
                        key={`dropdown-item-${searchTerm}`}
                        className='w-full h-10 flex items-center text-left px-6 bg-white cursor-pointer hover:bg-lightgray focus-visible:bg-lightgray'
                        onClick={() => {
                          setSearchTerm(searchTerm);
                          toggleDropdown(false);
                          onChange &&
                            onChange({
                              label: searchTerm,
                              value: searchTerm,
                            });
                        }}
                      >
                        {searchTerm}
                      </li>
                    )}
                </Scrollbar>
              )}
            </ul>,
            document.body
          )}
      </div>
    </div>
  );
};

export default SearchableDropDown;
